import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, ChevronDown, ChevronUp, Star, Code, Image, FileAudio, Database, Zap, BookOpen, PenTool, Video, HelpCircle } from 'lucide-react';
import { AITool } from '../types';
import { RelatedTools } from './RelatedTools';
import { aiTools } from '../data/aiTools';
import { JsonLd } from 'react-schemaorg';
import { SoftwareApplication } from 'schema-dts';

interface ToolCardProps {
  tool: AITool;
  index: number;
}

export const ToolCard: React.FC<ToolCardProps> = ({ tool, index }) => {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'getting-started'>('overview');
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  // Check if image is already cached
  useEffect(() => {
    if (imageRef.current && imageRef.current.complete) {
      setIsImageLoaded(true);
    }
  }, []);

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5
      }
    })
  };

  // Map use case IDs to icons
  const useCaseIcons: Record<string, React.ReactNode> = {
    'writing': <PenTool size={16} />,
    'images': <Image size={16} />,
    'video': <Video size={16} />,
    'audio': <FileAudio size={16} />,
    'data-analysis': <Database size={16} />,
    'automation': <Zap size={16} />,
    'research': <BookOpen size={16} />,
    'coding': <Code size={16} />
  };

  // Get pricing badge text and color
  const getPricingBadge = () => {
    switch (tool.pricingType) {
      case 'free':
        return { text: 'Free', bgColor: 'bg-green-100 dark:bg-green-900', textColor: 'text-green-800 dark:text-green-300' };
      case 'paid':
        return { text: 'Paid', bgColor: 'bg-purple-100 dark:bg-purple-900', textColor: 'text-purple-800 dark:text-purple-300' };
      case 'freemium':
        return { text: 'Freemium', bgColor: 'bg-blue-100 dark:bg-blue-900', textColor: 'text-blue-800 dark:text-blue-300' };
      default:
        return { text: '', bgColor: '', textColor: '' };
    }
  };

  const pricingBadge = getPricingBadge();
  
  // Pricing tooltip content
  const getPricingTooltipContent = () => {
    switch (tool.pricingType) {
      case 'free':
        return 'This tool is completely free to use with no payment required.';
      case 'paid':
        return 'This tool requires a paid subscription or one-time purchase.';
      case 'freemium':
        return 'This tool offers a free basic version with premium paid features available.';
      default:
        return '';
    }
  };

  // Generate schema.org JSON-LD data for this tool
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: tool.name,
    description: tool.description,
    applicationCategory: 'Artificial Intelligence',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: tool.pricingType === 'free' ? '0' : undefined,
      priceCurrency: 'USD'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: (tool.compatibilityScore / 20).toFixed(1),
      bestRating: '5',
      ratingCount: Math.floor(Math.random() * 100) + 50
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      custom={index}
      className="light-mode-card rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700 micro-bounce"
      tabIndex={0}
      id={`tool-${tool.id}`}
    >
      {/* Schema.org structured data */}
      <JsonLd<SoftwareApplication>
        item={schemaData as any}
      />

      {/* Card Header */}
      <div className="p-6 flex items-start justify-between">
        <div className="flex items-center">
          <div className="w-12 h-12 rounded-full overflow-hidden mr-4 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            {!isImageLoaded && (
              <div className="animate-pulse w-full h-full bg-gray-200 dark:bg-gray-600"></div>
            )}
            <img 
              ref={imageRef}
              src={tool.logo} 
              alt={`${tool.name} logo`} 
              className={`w-full h-full object-cover ${isImageLoaded ? 'opacity-100' : 'opacity-0'}`}
              loading="lazy"
              onLoad={() => setIsImageLoaded(true)}
              onError={(e) => {
                setIsImageLoaded(true);
                (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1679083216051-aa510a1a2c0e?q=80&w=100&auto=format&fit=crop';
              }}
            />
          </div>
          <div>
            <div className="flex items-center mb-1">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mr-2">{tool.name}</h3>
              <div className="tooltip">
                <span className={`text-xs font-medium italic px-2 py-1 rounded-full ${pricingBadge.bgColor} ${pricingBadge.textColor} flex items-center`}>
                  {pricingBadge.text}
                  <HelpCircle size={12} className="ml-1 cursor-help" />
                </span>
                <div className="tooltip-content bg-white dark:bg-gray-800 p-2 rounded-md shadow-lg text-xs text-gray-700 dark:text-gray-300 top-full mt-1 right-0">
                  {getPricingTooltipContent()}
                </div>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">{tool.description}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <a
            href={tool.affiliateLink || tool.tryNowLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Try Now <ExternalLink size={14} className="ml-1" />
          </a>
          <div className="flex items-center bg-indigo-100 dark:bg-indigo-900 px-3 py-1 rounded-full">
            <Star className="text-yellow-500 mr-1" size={16} />
            <span className="text-indigo-800 dark:text-indigo-300 font-medium text-sm">{tool.compatibilityScore}%</span>
          </div>
        </div>
      </div>

      {/* Use Case Icons */}
      <div className="px-6 pb-4 flex flex-wrap gap-2">
        {tool.useCases.map(useCase => (
          <div 
            key={useCase} 
            className="flex items-center bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-md text-xs"
            title={useCase.charAt(0).toUpperCase() + useCase.slice(1).replace('-', ' ')}
          >
            <span className="mr-1 text-indigo-600 dark:text-indigo-400">
              {useCaseIcons[useCase]}
            </span>
            <span className="text-gray-700 dark:text-gray-300 capitalize">
              {useCase.replace('-', ' ')}
            </span>
          </div>
        ))}
      </div>

      {/* Expandable Content */}
      <motion.div
        initial={false}
        animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
        className="overflow-hidden"
      >
        {/* Tabs */}
        <div className="border-t border-b border-gray-200 dark:border-gray-700 px-6">
          <div className="flex">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('getting-started')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'getting-started'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Getting Started
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6 bg-white dark:bg-gray-800">
          {activeTab === 'overview' && (
            <p className="text-gray-700 dark:text-gray-300 text-sm whitespace-pre-line">
              {tool.overview}
            </p>
          )}
          {activeTab === 'getting-started' && (
            <p className="text-gray-700 dark:text-gray-300 text-sm whitespace-pre-line">
              {tool.gettingStarted}
            </p>
          )}
        </div>
        
        {/* Related Tools Section */}
        <div className="px-6 pb-6">
          <RelatedTools currentTool={tool} allTools={aiTools} />
        </div>
      </motion.div>

      {/* Expand/Collapse Button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-center p-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
      >
        {expanded ? (
          <>
            <span className="text-sm font-medium mr-1">Show less</span>
            <ChevronUp size={16} />
          </>
        ) : (
          <>
            <span className="text-sm font-medium mr-1">Show more</span>
            <ChevronDown size={16} />
          </>
        )}
      </button>
    </motion.div>
  );
};